import { DateTime } from "luxon";
import { Component } from 'react';
import { Button, Card, Col, Container, ListGroup, Modal, Row, Pagination } from "react-bootstrap";
import { observe } from 'mobx';
import { Link } from 'react-router-dom';
import GameCard from '../components/GameCard';
import { NamEditor } from "../components/nes/NamEditor";
import NewGameDialog from "../components/NewGameDialog";
import BlogPosts from '../config/generated/BlogPosts';
import GameData from "../mobx/GameData";
import ApplicationState from "../mobx/ApplicationState";

import "./HomePage.scss";
import { CONSTANTS } from "../config/Constants";
import { getGameplayStyleFromKey } from "../data/GameplayStyle";
import slugify from "slugify";
import BaseGame, { coerceUnknownFields } from "../data/BaseGame";
import { v4 } from "uuid";
import RomAddressOffsetDialog from "../components/dialogs/RomAddressOffsetDialog";
import OldVersionDialog from "../components/dialogs/OldVersionDialog";

const POST_PAGE_SIZE = 3;

export default class HomePage extends Component {
    gameDataCleanup = null;

    confirmData = {
        resolve: null,
        reject: null,
        promise: null
    }

    constructor(props) {
        super(props);

        this.posts = BlogPosts.sort((a, b) => a.matter.date < b.matter.date ? 1 : -1);

        this.state = {
            myGames: [],
            myGamesLoading: true,
            popularGames: [],
            popularGamesLoading: true,

            awaitingConfirm: false,
            awaitingConfirmAction: 'Do it',
            dialogVisibility: {
                romOffset: false,
                oldVersion: false
            },
            postPage: 0,
            postLastPage: Math.ceil(this.posts.length / POST_PAGE_SIZE) - 1
        }
    }

    async componentDidMount() {
        this.reloadData();
        this.gameDataCleanup = observe(GameData, async () => { 
            await this.reloadData()
        });
    }

    componentWillUnmount() {
        if (this.gameDataCleanup) {
            this.gameDataCleanup();
        }
    }

    dismissNewUserPopup() {
        localStorage.setItem('RetroPuzzleMakerV2.IntroScreenDismissed', 'true');
        this.forceUpdate();
    }

    async reloadData() {
        try {
            const [ myGames, popularGames ] = await Promise.all([this.getMyGames(), this.getPopularGames()]);
            this.setState({
                myGames,
                myGamesLoading: false,
                popularGames,
                popularGamesLoading: false
            });
        } catch (e) {
            ApplicationState.addAlert('warning', 'Unable to get games to show. Try refreshing the page.');
            console.warn('Failed to get games', e);
            this.setState({
                myGames: [],
                myGamesLoading: false,
                popularGames: [],
                popularGamesLoading: false
            });    
        }
                
    }

    async getMyGames() {
        const theGames = GameData.userGames;

        return theGames.slice(0).sort((a, b) => b.updated - a.updated);

    }

    async getPopularGames() {
        const theGames = [];

        return theGames.sort((a, b) => b.updated - a.updated);
    }

    async deleteGame(game) {
        try {
            await this.showConfirm('Delete ' + game.title, game.title);
        } catch (e) {
            // Delete cancelled. Bail out.
            return;
        }
        GameData.removeGame(game.id);
        ApplicationState.addAlert('success', 'Game deleted!');
    }

    async exportGame(game) {
        const a = document.createElement('a');
        a.setAttribute('href', "data:text/plain;charset=utf-8," + encodeURIComponent(JSON.stringify(game, null, 4)));
        a.setAttribute('download', slugify(game.title) + '.json');
        a.click();
    }

    async doImport(game) {
        const upload = document.createElement('input');
        upload.setAttribute('type', 'file');
        upload.setAttribute('accept', '.json');
        
        let file;
        try {
            file = await new Promise((resolve, reject) => {
                upload.onchange = (e) => {
                    if (e.target && e.target.files.length) {
                        resolve(e.target.files[0]);
                    } else {
                        reject(e);
                    }
                }
                upload.click();
            });
        } catch (e) {
            console.info('File upload cancelled, doing nothing', e);
            return;
        }

        const reader = new FileReader();
        reader.readAsText(file, 'UTF-8');

        const data = await new Promise((resolve, reject) => {
            reader.onload = readerEvent => {
                resolve(readerEvent.target.result);
            }
        });

        let gameData;
        
        try {
            gameData = JSON.parse(data);
        } catch (e) {
            console.error('Failed parsing uploaded file! Likely a syntax error in the file', e);
            ApplicationState.addAlert('danger', 'Unable to parse uploaded file - check the syntax of the file if it was changed. More information in browser console.');
            return;
        }

        if (GameData.userGames.find(a => a.title.toLowerCase() === gameData.title.toLowerCase())) {
            ApplicationState.addAlert('warning', 'You already have a game titled "' + gameData.title + '" - please rename the existing game before importing!');
            return;
        }

        const theGame = BaseGame(v4(), gameData);
        await GameData.addGame(theGame);
        GameData.setCurrentGame(gameData.id);
        ApplicationState.addAlert('success', 'Game loaded!');

    }

    showConfirm(action, gameName) {
        this.confirmData.promise = new Promise((resolve, reject) => {
            this.confirmData.resolve = resolve;
            this.confirmData.reject = reject;
        });
        this.setState({
            awaitingConfirm: true,
            awaitingConfirmAction: action,
            awaitingConfirmData: gameName
        });

        return this.confirmData.promise;
    }

    cancelConfirm() {
        this.confirmData.reject();
        this.confirmData = {
            promise: null,
            resolve: null, 
            reject: null
        };
        this.setState({awaitingConfirm: false})
    }

    continueConfirm() {
        this.confirmData.resolve();
        this.confirmData = {
            promise: null,
            resolve: null, 
            reject: null
        };
        this.setState({awaitingConfirm: false})
    }

    disableLinkIfNoGame(e) {
        if (!GameData.currentGame) {
            e.preventDefault();
            ApplicationState.addAlert('info', 'Please create a game first.');
        }
    }

    async downloadPatchableData(e) {
        const game = coerceUnknownFields(GameData.currentGame);
        e.preventDefault();
        const string = await GameData.generatePatchableDataForCurrentRom();

        const a = document.createElement('a');
        a.setAttribute('href', 'data:text/plain;charset=utf-8,' + encodeURIComponent(string));
        a.setAttribute('download', (game.smallRom ? 'patchable_data_small.asm' : 'patchable_data.asm'));
        a.click();
    }

    doDialog(event, name, showHide) {
        event?.preventDefault();
        this.setState({dialogVisibility: {...this.state.dialogVisibility, [name]: showHide}});
    }

    render() {
        const myGameList = [];
        if (this.state.myGamesLoading) {
            myGameList.push(<GameCard game={false} showOpen={true} key="my-games-unloaded" isMini={true} />);
        } else if (this.state.myGames.length > 0) {
            this.state.myGames.forEach(game => {
                myGameList.push(<GameCard game={game} showOpen={true} key={'my-games-' + game.title} isMini={true} doDelete={() => this.deleteGame(game)} doExport={() => this.exportGame(game)} />);
            });
        } else {
            myGameList.push(<li key="my-games-zero" className="d-flex justify-content-between align-items-start game-card list-group-item text-muted">No games created. Click "New Game" below!</li>)
        }

        let rightSideCard;
        if (CONSTANTS.PopularGamesEnabled) {
            const popGameList = [];
            if (this.state.popularGamesLoading) {
                for (let i = 0; i < 3; ++i) {
                    popGameList.push(<GameCard game={false} key={"popular-games-unloaded-" + i} isMini={true} />);
                }
            } else {
                this.state.popularGames.forEach(game => {
                    popGameList.push(<GameCard game={game} key={'popular-games-' + game.title} isMini={true} />);
                });
            }
            rightSideCard = (
                <Card>
                    <Card.Header>Popular Games</Card.Header>
                    <Card.Body>
                        <Card.Text as="div">
                            <ListGroup as="ul">
                                {{popGameList}}
                            </ListGroup>
                        </Card.Text>
                    </Card.Body>
                </Card>
            );
        } else {
            let gameInfoTable = [
                <tr key="no-game-info"><td className="game-info-plain">No game Loaded!</td></tr>,
                <tr key="no-game-info-2"><td className="game-info-plain padded">
                    <Button className="mx-2" variant="outline-info" onClick={() => this.doImport()}>Import Game</Button>
                    <NewGameDialog />
                </td></tr>
            ]
            if (GameData.currentGame) {
                const cg = GameData.currentGame;
                gameInfoTable = [
                    ['Author', cg.author],
                    ['Levels', cg.lastLevel + 1],
                    ['Default level style', getGameplayStyleFromKey(cg.defaultLevelStyle).toLowerCase().replace(/_/g, ' ')],
                    ['Created on', DateTime.fromMillis(cg.created).toLocaleString(DateTime.DATE_MED)],
                    ['Last update', DateTime.fromMillis(cg.updated).toLocaleString(DateTime.DATE_MED)],
                ].map(entry => (
                    <tr key={"game-info-" + entry[0]}>
                        <td><span>{entry[0]}</span>:</td>
                        <td>{entry[1]}</td>
                    </tr>
                ))
                gameInfoTable.push(
                    <tr key="title-screen" className="title-screen">
                        <td colSpan="2">
                            <div className="title-screen">
                                <NamEditor fieldName="titleScreenData" readOnly={true} key={"title-" + GameData.currentGame.id} />
                            </div>
                        </td>

                    </tr>
                )
            }
            rightSideCard = (
                <Card>
                    <Card.Header>Current Game</Card.Header>
                    <Card.Body>
                        <Card.Text as="div">
                            {GameData.currentGame ? (<Card.Title className="mb-3">{GameData.currentGame.title}</Card.Title>) : ''}
                            <ListGroup as="ul">
                                <table>
                                    <tbody>
                                        {gameInfoTable}
                                    </tbody>
                                </table>
                            </ListGroup>
                        </Card.Text>
                    </Card.Body>
                </Card>

            )
        }

        let introTile = <></>
        if (localStorage.getItem('RetroPuzzleMakerV2.IntroScreenDismissed') !== 'true') {
            introTile = <>
                <Row className="home-row intro-row">
                    <Col>
                        <Card>
                            <Card.Header>
                                Introduction
                                <Button className="dismiss" variant="outline-secondary" size="sm" onClick={() => this.dismissNewUserPopup()}>Dismiss</Button>
                            </Card.Header>
                            <Card.Body>
                                <Card.Title>Welcome to Retro Puzzle Maker!</Card.Title>
                                <Card.Text>
                                    This is a new tool meant to help you create new puzzle games you can play on an NES! You can download
                                    a rom, or play using the built-in emulator. You can also share your game publicly with a share link!
                                </Card.Text>
                                <Card.Text>
                                    After creating a game, a menu of options will appear to the left. A good first place to explore may be 
                                    the <Link onClick={e => this.disableLinkIfNoGame(e)} to="/builder/level">Level Designer</Link>.
                                </Card.Text>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </>
        }


        return (
            <div className="home-page">
                <Modal backdrop="static" show={this.state.awaitingConfirm} onClose={() => this.cancelConfirm()} aria-labelledby="Delete Game">
                    <Modal.Header closeButton>
                        <Modal.Title>Delete Game?</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <p>Are you sure you want to delete <strong>{this.state.awaitingConfirmData}</strong>? The game cannot be recovered after deletion!</p>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => this.cancelConfirm()}>Cancel</Button>
                        <Button variant="danger" onClick={() => this.continueConfirm()}>{this.state.awaitingConfirmAction}</Button>
                    </Modal.Footer>
                </Modal>
                <Container>
                    {introTile}
                    <Row className="home-row">
                        <Col sm={6}>
                            <Card>
                                <Card.Header>Your Games</Card.Header>
                                <Card.Body>
                                    <Card.Text as="div">
                                        <ListGroup as="ul">
                                            {myGameList}
                                        </ListGroup>
                                    </Card.Text>
                                    <div className="mt-3 button-footer-bar">
                                        <Button className="mx-2" variant="outline-info" onClick={() => this.doImport()}>Import Game</Button>
                                        <NewGameDialog />
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col sm={6}>
                            {rightSideCard}
                        </Col>
                    </Row>
                    <Row className="home-row">
                        <Col sm={9}>
                            <Card>
                                <Card.Header>Latest News</Card.Header>
                                <Card.Body>
                                    {this.posts.slice(this.state.postPage*POST_PAGE_SIZE, this.state.postPage*POST_PAGE_SIZE + POST_PAGE_SIZE).map((post, id) => <div key={`post-${id}`}>
                                        <Card.Title>{post.matter.title}</Card.Title>
                                        <Card.Text>
                                            <small>
                                                {post.matter.version}
                                                {' '}
                                                &#8226; 
                                                {' '}
                                                {DateTime.fromISO(post.matter.date).toLocaleString(DateTime.DATE_MED)} 
                                            </small>
                                            <span dangerouslySetInnerHTML={{__html: post.parsed}}></span>
                                        </Card.Text>
                                        <hr className="news-sep" />
                                    </div>)}
                                    <div className="pagination-holder">
                                        <Pagination>
                                            <Pagination.First onClick={() => this.setState({postPage: 0})} disabled={this.state.postPage === 0} />
                                            <Pagination.Prev onClick={() => this.setState({postPage: this.state.postPage - 1})} disabled={this.state.postPage === 0} />
                                            {[...Array(this.state.postLastPage +1).keys()].map(pg => (
                                                <Pagination.Item key={"page-" + pg} onClick={() => this.setState({postPage: pg})} active={this.state.postPage === pg}>{pg+1}</Pagination.Item>
                                            ))}
                                            <Pagination.Next onClick={() => this.setState({postPage: this.state.postPage + 1})} disabled={this.state.postPage === this.state.postLastPage} />
                                            <Pagination.Last onClick={() => this.setState({postPage: this.state.postLastPage})} disabled={this.state.postPage === this.state.postLastPage} />                                    
                                        </Pagination>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col>
                            <Card>
                                <Card.Header>Downloadable Version</Card.Header>
                                <Card.Body>
                                    <p>Download this app to run on your own computer, even offline!</p>
                                    <ul>
                                        {Object.keys(CONSTANTS.Binaries).map(binName => {
                                            const binStr = CONSTANTS.Binaries[binName];

                                            return <li key={binName}><a rel="noreferrer" href={CONSTANTS.LatestBinariesUrl + binStr} target="_blank">{binName}</a></li>
                                        })}
                                    </ul>
                                    <p><a rel="noreferrer" href="/" onClick={e => this.doDialog(e, 'oldVersion', true)}>Older versions</a></p>
                                </Card.Body>
                            </Card>
                            <Card className="mt-2">
                                <Card.Header>Developer Tools</Card.Header>
                                {(GameData.currentGame ? (<Card.Body>
                                    <p>Want to do more? Use these to take your game to the next level.</p>

                                    <ul>
                                        <li><a rel="noreferrer" href={CONSTANTS.GithubRepoUrl} target="_blank">Base game source (Github)</a></li>
                                        <li><a rel="noreferrer" href="/" onClick={e => { this.downloadPatchableData(e); }}>Download patchable_data.asm</a></li>
                                        <li><a rel="noreferrer" href="/" onClick={e => { e.preventDefault(); window.getIpsData(); }}>Download IPS Patch</a></li>
                                        <li><a rel="noreferrer" href="/" onClick={e => this.doDialog(e, 'romOffset', true)}>Rom address offsets</a></li>
                                    </ul>
                                </Card.Body>
                                ) : (
                                    <Card.Body>
                                        <p><em>Load a game, and developer tools will appear here.</em></p>
                                    </Card.Body>
                                ))}
                            </Card>
                        </Col>
                    </Row>
                </Container>
                <RomAddressOffsetDialog visible={this.state.dialogVisibility.romOffset} onClose={e => this.doDialog(e, 'romOffset', false)} />
                <OldVersionDialog visible={this.state.dialogVisibility.oldVersion} onClose={e => this.doDialog(e, 'oldVersion', false)} />
            </div>
        );
    }
}